import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Calendar, MapPin, Users, ArrowRight, Trophy, Quote, Star } from "lucide-react";
import useSeo from "@/components/useSeo";

const StarRow = ({ value, size = 14 }) => (
  <span className="inline-flex gap-0.5" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={size} className={n <= value ? "fill-current text-amber-400" : "text-neutral-600"} />
    ))}
  </span>
);

export default function ClubSite() {
  const { clubId } = useParams();
  const [data, setData] = useState(null);
  const [cf, setCf] = useState({ name: "", email: "", message: "", website: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [rv, setRv] = useState({ author: "", role: "", quote: "", rating: 0, website: "" });
  const [rvSent, setRvSent] = useState(false);
  const submitReview = async (e) => {
    e.preventDefault();
    if (!rv.author || !rv.quote) return;
    try { await api.post(`/clubs/${clubId}/review`, rv); setRvSent(true); setRv({ author: "", role: "", quote: "", rating: 0, website: "" }); }
    catch (err) { toast.error(err?.response?.status === 429 ? "Too many submissions — please try again later." : "Couldn't submit your review. Please try again."); }
  };
  const submitContact = async (e) => {
    e.preventDefault();
    if (!cf.name || !cf.email || !cf.message) return;
    setSending(true);
    try { await api.post(`/clubs/${clubId}/contact`, cf); setSent(true); setCf({ name: "", email: "", message: "", website: "" }); }
    catch (err) { toast.error(err?.response?.status === 429 ? "Too many submissions — please try again later." : "Couldn't send your message. Please try again."); }
    finally { setSending(false); }
  };

  useEffect(() => {
    api.get(`/clubs/${clubId}/site`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [clubId]);
  useSeo({
    title: data?.club?.name ? `${data.club.name} · SphereHyx` : "Club — SphereHyx",
    description: data?.club?.name
      ? `${data.club.name} — join the club, view teams, schedules and news. Powered by SphereHyx.`
      : "Sports club home page on SphereHyx.",
    canonical: `https://spherehyx.com/club/${clubId}`,
    image: data?.club?.logo || undefined,
  });

  if (data === false) return <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-400">Club not found.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-neutral-950"><div className="h-8 w-8 border-2 border-white/70 border-t-transparent rounded-full animate-spin" /></div>;

  const c = data.club;
  const primary = c.primary_color || "#007AFF";
  const secondary = c.secondary_color || "#10B981";

  return (
    <div className="min-h-screen bg-neutral-950 text-white" data-testid="club-site">
      <div className="relative overflow-hidden" style={c.hero_photo ? { backgroundImage: `url(${c.hero_photo})`, backgroundSize: "cover", backgroundPosition: "center" } : { background: `linear-gradient(135deg, ${primary}, ${secondary})` }}>
        {c.hero_photo && <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${primary}dd, ${secondary}cc)` }} />}
        <div className="max-w-4xl mx-auto px-6 py-24 text-center relative z-10">
          {c.logo ? <img src={c.logo} alt="" className="h-24 w-24 rounded-2xl object-cover border-2 border-white/30 mx-auto shadow-xl" />
            : <div className="h-24 w-24 rounded-2xl bg-white/15 flex items-center justify-center text-4xl font-black mx-auto">{c.name?.[0]}</div>}
          <h1 className="mt-6 text-4xl sm:text-6xl font-black tracking-tight" data-testid="club-site-name">{c.name}</h1>
          <p className="mt-3 text-lg text-white/90 capitalize">{c.sport?.replace("_", " ")} club{c.country ? ` \u00b7 ${c.country}` : ""}</p>
          {data.rating_avg && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5" data-testid="club-site-hero-rating">
              <StarRow value={Math.round(data.rating_avg)} size={16} />
              <span className="text-sm font-semibold">{data.rating_avg}</span>
              <span className="text-sm text-white/70">({data.rating_count} review{data.rating_count === 1 ? "" : "s"})</span>
            </div>
          )}
          <div className="mt-8">
            <Link to={`/join/${clubId}`} data-testid="club-site-join-btn"
              className="inline-flex items-center gap-2 bg-white text-neutral-900 font-semibold px-7 py-3.5 rounded-full hover:scale-105 transition-transform duration-200 shadow-lg">
              Join {c.name} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-14 space-y-14">
        {(c.about || c.contact_email || c.contact_phone) && (
          <section data-testid="club-site-about">
            {c.about && <><h2 className="text-2xl font-bold mb-4">About</h2><p className="text-neutral-300 leading-relaxed whitespace-pre-wrap mb-6">{c.about}</p></>}
            {(c.contact_email || c.contact_phone) && (
              <div className="flex flex-wrap gap-5 text-sm text-neutral-300">
                {c.contact_email && <a href={`mailto:${c.contact_email}`} className="hover:text-white underline underline-offset-4">{c.contact_email}</a>}
                {c.contact_phone && <span>{c.contact_phone}</span>}
              </div>
            )}
          </section>
        )}
        {data.announcements?.length > 0 && (
          <section data-testid="club-site-announcements">
            <h2 className="text-2xl font-bold mb-5">Latest news</h2>
            <div className="space-y-3">
              {data.announcements.map((a) => (
                <div key={a.id} className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid={`announcement-${a.id}`}>
                  <p className="font-semibold">{a.title}</p>
                  <p className="text-sm text-neutral-300 mt-1 whitespace-pre-wrap">{a.body}</p>
                  <p className="text-xs text-neutral-500 mt-2">{a.created_at?.slice(0, 10)}</p>
                </div>
              ))}
            </div>
          </section>
        )}
        <section>
          <h2 className="flex items-center gap-2 text-2xl font-bold mb-5"><Calendar size={22} style={{ color: secondary }} /> Upcoming schedule</h2>
          {data.schedule.length === 0 ? <p className="text-neutral-500">No upcoming events posted yet.</p> : (
            <div className="space-y-3">
              {data.schedule.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 p-5" data-testid={`club-site-event-${m.id}`}>
                  <div>
                    <p className="font-semibold">{m.team ? `${m.team} ` : ""}vs {m.opponent || "TBD"}</p>
                    <p className="text-sm text-neutral-400 flex items-center gap-1.5 mt-1"><MapPin size={13} /> {m.location || "TBD"}</p>
                  </div>
                  <span className="text-sm text-neutral-300 shrink-0">{m.date || "\u2014"}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {data.teams.length > 0 && (
          <section>
            <h2 className="flex items-center gap-2 text-2xl font-bold mb-5"><Trophy size={22} style={{ color: secondary }} /> Teams</h2>
            <div className="flex flex-wrap gap-3">
              {data.teams.map((t) => (
                <span key={t.id} className="rounded-full border border-white/15 bg-white/5 px-5 py-2 text-sm">{t.name}{t.age_group ? ` \u00b7 ${t.age_group}` : ""}</span>
              ))}
            </div>
          </section>
        )}

        {data.testimonials?.length > 0 && (
          <section data-testid="club-site-testimonials">
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <h2 className="flex items-center gap-2 text-2xl font-bold"><Quote size={22} style={{ color: secondary }} /> What families say</h2>
              {data.rating_avg && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 text-amber-300 px-3 py-1 text-sm font-semibold" data-testid="club-site-rating-badge">
                  <Star size={15} className="fill-current" /> {data.rating_avg}<span className="text-amber-300/70 font-normal">/5 · {data.rating_count}</span>
                </span>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {data.testimonials.map((t) => (
                <figure key={t.id} className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid={`testimonial-${t.id}`}>
                  {t.rating > 0 && <div className="mb-3"><StarRow value={t.rating} /></div>}
                  <blockquote className="text-neutral-200 leading-relaxed">"{t.quote}"</blockquote>
                  <figcaption className="mt-3 text-sm text-neutral-400">— {t.author}{t.role ? `, ${t.role}` : ""}</figcaption>
                  {t.reply && (
                    <div className="mt-4 rounded-lg border-l-2 pl-3 py-1" style={{ borderColor: secondary }} data-testid={`testimonial-reply-${t.id}`}>
                      <p className="text-xs font-semibold" style={{ color: secondary }}>Response from {c.name}</p>
                      <p className="text-sm text-neutral-300 mt-1 whitespace-pre-wrap">{t.reply}</p>
                    </div>
                  )}
                </figure>
              ))}
            </div>
          </section>
        )}

        <section data-testid="club-site-review">
          <h2 className="text-2xl font-bold mb-5">Leave a review</h2>
          {rvSent ? (
            <p className="text-neutral-300" data-testid="review-sent">Thanks! Your review was submitted for approval.</p>
          ) : (
            <form onSubmit={submitReview} className="grid sm:grid-cols-2 gap-3 max-w-2xl">
              <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={rv.website} onChange={(e) => setRv({ ...rv, website: e.target.value })} className="hidden" data-testid="review-hp" />
              <div className="sm:col-span-2 flex items-center gap-1" data-testid="review-rating">
                <span className="text-sm text-neutral-400 mr-2">Your rating</span>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} onClick={() => setRv({ ...rv, rating: n })} data-testid={`review-star-${n}`} className="p-0.5" aria-label={`${n} star${n === 1 ? "" : "s"}`}>
                    <Star size={24} className={n <= rv.rating ? "fill-current text-amber-400" : "text-neutral-600 hover:text-neutral-400"} />
                  </button>
                ))}
              </div>
              <input required value={rv.author} onChange={(e) => setRv({ ...rv, author: e.target.value })} placeholder="Your name" className="h-11 rounded-lg bg-white/5 border border-white/15 px-3 text-sm text-white placeholder:text-neutral-500" data-testid="review-author" />
              <input value={rv.role} onChange={(e) => setRv({ ...rv, role: e.target.value })} placeholder="Role (e.g. Parent) — optional" className="h-11 rounded-lg bg-white/5 border border-white/15 px-3 text-sm text-white placeholder:text-neutral-500" data-testid="review-role" />
              <textarea required value={rv.quote} onChange={(e) => setRv({ ...rv, quote: e.target.value })} placeholder="Share your experience..." rows={3} className="sm:col-span-2 rounded-lg bg-white/5 border border-white/15 p-3 text-sm text-white placeholder:text-neutral-500" data-testid="review-quote" />
              <button type="submit" className="sm:col-span-2 h-11 rounded-full text-white font-semibold hover:opacity-90 transition-opacity" style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }} data-testid="review-submit">Submit review</button>
            </form>
          )}
        </section>

        {data.gallery?.length > 0 && (
          <section data-testid="club-site-gallery">
            <h2 className="text-2xl font-bold mb-5">Gallery</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {data.gallery.map((g) => (
                <figure key={g.id} className="rounded-2xl overflow-hidden border border-white/10 bg-white/5" data-testid={`gallery-${g.id}`}>
                  <img src={g.image} alt={g.caption || ""} className="w-full h-40 object-cover" />
                  {g.caption && <figcaption className="p-2 text-xs text-neutral-400">{g.caption}</figcaption>}
                </figure>
              ))}
            </div>
          </section>
        )}

        {data.roster_public && (
          <section>
            <h2 className="flex items-center gap-2 text-2xl font-bold mb-5"><Users size={22} style={{ color: secondary }} /> Roster</h2>
            {data.roster.length === 0 ? <p className="text-neutral-500">No athletes yet.</p> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {data.roster.map((p, i) => (
                  <div key={i} className="rounded-2xl border border-white/10 bg-white/5 p-4 flex items-center gap-3" data-testid={`club-site-player-${i}`}>
                    {p.photo ? <img src={p.photo} alt="" className="h-11 w-11 rounded-full object-cover" />
                      : <div className="h-11 w-11 rounded-full flex items-center justify-center font-bold text-white" style={{ background: primary }}>{p.name?.[0]}</div>}
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{p.name}</p>
                      <p className="text-xs text-neutral-400 truncate">{p.position || "Athlete"}{p.jersey_number ? ` \u00b7 #${p.jersey_number}` : ""}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
        <section data-testid="club-site-contact">
          <h2 className="text-2xl font-bold mb-5">Contact the club</h2>
          {sent ? (
            <p className="text-neutral-300" data-testid="contact-sent">Thanks! Your message has been sent to the club.</p>
          ) : (
            <form onSubmit={submitContact} className="grid sm:grid-cols-2 gap-3 max-w-2xl">
              <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={cf.website} onChange={(e) => setCf({ ...cf, website: e.target.value })} className="hidden" data-testid="contact-hp" />
              <input required value={cf.name} onChange={(e) => setCf({ ...cf, name: e.target.value })} placeholder="Your name" className="h-11 rounded-lg bg-white/5 border border-white/15 px-3 text-sm text-white placeholder:text-neutral-500" data-testid="contact-name" />
              <input required type="email" value={cf.email} onChange={(e) => setCf({ ...cf, email: e.target.value })} placeholder="Your email" className="h-11 rounded-lg bg-white/5 border border-white/15 px-3 text-sm text-white placeholder:text-neutral-500" data-testid="contact-email" />
              <textarea required value={cf.message} onChange={(e) => setCf({ ...cf, message: e.target.value })} placeholder="How can we help?" rows={4} className="sm:col-span-2 rounded-lg bg-white/5 border border-white/15 p-3 text-sm text-white placeholder:text-neutral-500" data-testid="contact-message" />
              <button type="submit" disabled={sending} className="sm:col-span-2 h-11 rounded-full text-white font-semibold hover:opacity-90 transition-opacity" style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }} data-testid="contact-submit">{sending ? "Sending..." : "Send message"}</button>
            </form>
          )}
        </section>
      </div>

      <footer className="border-t border-white/10 py-8 text-center text-sm text-neutral-500 space-y-2">
        <div>
          <Link to={`/join/${clubId}`} className="underline hover:text-white">Register your athlete</Link> · Powered by SphereHyx
        </div>
        <div className="flex items-center justify-center gap-4 text-xs">
          <a href="/privacy" target="_blank" rel="noreferrer" className="text-neutral-400 underline hover:text-white" data-testid="clubsite-privacy-link">Privacy Policy</a>
          <a href="/terms" target="_blank" rel="noreferrer" className="text-neutral-400 underline hover:text-white" data-testid="clubsite-terms-link">Terms &amp; Conditions</a>
        </div>
      </footer>
    </div>
  );
}
